import { Formik } from 'formik';
import { object, string } from 'yup';

import { useChangeVerificationEmailMutation } from '@app/api/emailConfirmation';
import { isValidationErrorResponse } from '@app/api/utils';
import isEmail from '@app/utils/string/isEmail';

import Modal from '../Modal';
import { showUnexpectedAuthErrorToast } from '../ToastAuth';
import ChangeEmailModal from './ChangeEmailModal';

export type SubmitValues = {
    email: string;
};

const EmailVerificationSchema = () =>
    object().shape({
        email: string()
            .required('E-mail is required.')
            .test('email-validator', 'E-mail is invalid.', (value) => {
                if (!value) {
                    return false;
                }

                return isEmail(value);
            }),
    });

type ChangeEmailModalContainerProps = {
    initialEmail: string;
    onClose: () => void;
    onChange: (email: string) => void;
};

const ChangeEmailModalContainer = ({ onChange, initialEmail = '', onClose }: ChangeEmailModalContainerProps) => {
    const [changeVerificationEmail, { isLoading: changeLoading }] = useChangeVerificationEmailMutation();

    const onSubmit = async ({ email }: SubmitValues, setFieldError: (field: string, message: string) => void) => {
        const response = await changeVerificationEmail({ email });

        if ('error' in response) {
            const { error } = response;

            if (isValidationErrorResponse(error)) {
                setFieldError('email', error.data.error.violations?.email[0] || '');
            } else {
                showUnexpectedAuthErrorToast();
            }

            return;
        }

        onChange(email);
        onClose();
    };

    return (
        <Modal title="Change your registration e-mail" onCloseHandler={onClose}>
            <Formik<SubmitValues>
                initialValues={{ email: initialEmail }}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={EmailVerificationSchema}
                onSubmit={(values, { setFieldError }) => {
                    onSubmit(values, setFieldError);
                }}
            >
                <ChangeEmailModal changeLoading={changeLoading} />
            </Formik>
        </Modal>
    );
};

export default ChangeEmailModalContainer;
